import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import SidebarLayout from "./SidebarLayout";
import PackageAgreementModal from "../staff/PackageAgreementModal";
import "./shopStyles.css";

/**
 * Shop → Settings → Packages.
 *
 * This is where packages are switched on (D32), and where each professional's share of a package
 * is set (D18).
 *
 * Two rules the UI has to respect, both enforced server-side:
 *   1. Packages cannot be switched ON until the CURRENT agreement version is accepted. The server
 *      reads the version from settings, so this screen never sends one.
 *   2. The share is PER PROFESSIONAL. There is no single package-wide figure, because there is no
 *      pair of percentages that is correct for every professional.
 */
const PackageSettings = () => {
  const navigate = useNavigate();

  const API_BASE_URL =
    process.env.REACT_APP_API_BASE_URL || "https://api.ogestyle.com";

  const [settings, setSettings] = useState(null);
  const [packages, setPackages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [showAgreement, setShowAgreement] = useState(false);

  const getAuthHeaders = useCallback(() => {
    const token = localStorage.getItem("ogestyle_token");
    if (!token) {
      navigate("/login");
      return null;
    }
    return {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    };
  }, [navigate]);

  const load = useCallback(async () => {
    const headers = getAuthHeaders();
    if (!headers) return;

    try {
      const settingsResponse = await fetch(
        `${API_BASE_URL}/api/packages/settings`,
        { headers },
      );
      if (!settingsResponse.ok) {
        throw new Error("Could not load package settings.");
      }
      setSettings(await settingsResponse.json());

      // The staff view of this route returns every package with its subscribers and its
      // `sellability` reasons, which is what the shop needs to act on.
      const meResponse = await fetch(`${API_BASE_URL}/api/staff/me`, { headers });
      const meData = meResponse.ok ? await meResponse.json() : {};
      const shopId = meData.staff?.activeLocationId;

      if (!shopId) {
        setPackages([]);
        return;
      }

      const packagesResponse = await fetch(
        `${API_BASE_URL}/api/packages/shop/${shopId}?activeOnly=false`,
        { headers },
      );
      const packagesData = packagesResponse.ok
        ? await packagesResponse.json()
        : {};
      setPackages(packagesData.packages || []);
    } catch (err) {
      setError(err.message || "Could not load package settings.");
    } finally {
      setLoading(false);
    }
  }, [API_BASE_URL, getAuthHeaders]);

  useEffect(() => {
    load();
  }, [load]);

  const agreementAccepted = Boolean(settings?.agreementAccepted);
  const packagesEnabled = Boolean(settings?.packagesEnabled);

  const flash = (message) => {
    setNotice(message);
    setTimeout(() => setNotice(""), 4000);
  };

  const setEnabled = async (enabled) => {
    if (enabled && !agreementAccepted) {
      setError("");
      setShowAgreement(true);
      return;
    }

    const headers = getAuthHeaders();
    if (!headers) return;

    setBusy(true);
    setError("");
    try {
      const response = await fetch(
        `${API_BASE_URL}/api/packages/settings/packages-enabled`,
        {
          method: "POST",
          headers,
          body: JSON.stringify({ enabled }),
        },
      );
      const body = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(body.message || body.error || "Could not update settings.");
      }
      await load();
      flash(body.message || "Settings updated.");
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  const acceptAgreement = async () => {
    const headers = getAuthHeaders();
    if (!headers) return;

    setBusy(true);
    setError("");
    try {
      const response = await fetch(
        `${API_BASE_URL}/api/packages/agreement/accept`,
        { method: "POST", headers },
      );
      const body = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(body.message || body.error || "Could not record acceptance.");
      }
      setShowAgreement(false);
      await setEnabled(true);
    } catch (err) {
      setError(err.message);
      setBusy(false);
    }
  };

  const card = {
    background: "white",
    borderRadius: "12px",
    padding: "20px",
    boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
    marginBottom: "20px",
  };

  return (
    <SidebarLayout>
      <div style={{ padding: "24px", maxWidth: "960px" }}>
        <h1 style={{ fontSize: "26px", fontWeight: "700", margin: "0 0 6px" }}>
          📦 Packages
        </h1>
        <p style={{ color: "#6b7280", margin: "0 0 20px" }}>
          Switch packages on, and set what each professional is paid.
        </p>

        {error ? (
          <div
            style={{
              background: "#fef2f2",
              border: "1px solid #fecaca",
              color: "#991b1b",
              padding: "12px 14px",
              borderRadius: "8px",
              marginBottom: "16px",
              fontSize: "13px",
            }}
          >
            {error}
          </div>
        ) : null}

        {notice ? (
          <div
            style={{
              background: "#f0fdf4",
              border: "1px solid #bbf7d0",
              color: "#166534",
              padding: "12px 14px",
              borderRadius: "8px",
              marginBottom: "16px",
              fontSize: "13px",
            }}
          >
            {notice}
          </div>
        ) : null}

        {loading ? (
          <div style={card}>Loading…</div>
        ) : (
          <>
            <div style={card}>
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  gap: "16px",
                  flexWrap: "wrap",
                }}
              >
                <div style={{ flex: 1, minWidth: "260px" }}>
                  <h2 style={{ fontSize: "16px", fontWeight: "700", margin: "0 0 4px" }}>
                    Offer packages
                  </h2>
                  <p style={{ fontSize: "13px", color: "#6b7280", margin: 0 }}>
                    {packagesEnabled
                      ? "Packages are switched on for your business."
                      : "Packages are switched off. While off, your packages are hidden from customers."}
                  </p>
                </div>
                <button
                  type="button"
                  disabled={busy}
                  onClick={() => setEnabled(!packagesEnabled)}
                  style={{
                    padding: "10px 18px",
                    borderRadius: "8px",
                    border: "none",
                    background: packagesEnabled ? "#dc2626" : "#3b82f6",
                    color: "white",
                    fontSize: "14px",
                    fontWeight: "600",
                    cursor: busy ? "not-allowed" : "pointer",
                  }}
                >
                  {packagesEnabled ? "Switch off" : "Switch on"}
                </button>
              </div>

              <div
                style={{
                  marginTop: "14px",
                  paddingTop: "14px",
                  borderTop: "1px solid #f1f5f9",
                  fontSize: "13px",
                  color: "#374151",
                }}
              >
                <div style={{ marginBottom: "6px" }}>
                  <strong>Agreement:</strong>{" "}
                  {agreementAccepted ? (
                    <span style={{ color: "#166534" }}>
                      accepted — version {settings?.packageAgreement?.version}
                      {settings?.packageAgreement?.acceptedAt
                        ? ` on ${new Date(settings.packageAgreement.acceptedAt).toLocaleDateString()}`
                        : ""}
                    </span>
                  ) : settings?.packageAgreement?.version ? (
                    <span style={{ color: "#b45309" }}>
                      version {settings.packageAgreement.version} is no longer current. The current
                      agreement ({settings?.currentVersion}) must be accepted.
                    </span>
                  ) : (
                    <span style={{ color: "#b45309" }}>
                      not accepted — the current agreement must be accepted before packages can be
                      switched on.
                    </span>
                  )}
                </div>
                <button
                  type="button"
                  onClick={() => {
                    setError("");
                    setShowAgreement(true);
                  }}
                  style={{
                    background: "none",
                    border: "none",
                    padding: 0,
                    color: "#2563eb",
                    fontSize: "13px",
                    fontWeight: "600",
                    cursor: "pointer",
                    textDecoration: "underline",
                  }}
                >
                  {agreementAccepted
                    ? "Review the agreement"
                    : "Review and accept the agreement"}
                </button>
              </div>
            </div>

            <h2 style={{ fontSize: "18px", fontWeight: "700", margin: "0 0 12px" }}>
              Who can deliver your packages
            </h2>
            <p style={{ fontSize: "13px", color: "#6b7280", margin: "0 0 16px" }}>
              What each professional is paid per package session comes from their existing
              commission split, which you set on the Professionals page. There is no separate
              package rate to maintain — a package session pays the same as a normal booking.
            </p>

            {packages.length === 0 ? (
              <div style={card}>
                <p style={{ margin: 0, fontSize: "14px", color: "#6b7280" }}>
                  You have no packages yet. Create one from Package Management.
                </p>
              </div>
            ) : null}

            {packages.map((pkg) => {
              const sellability = pkg.sellability;
              const subscribers = pkg.subscriberDetails || [];

              return (
                <div key={pkg._id} style={card}>
                  <div style={{ marginBottom: "12px" }}>
                    <h3 style={{ fontSize: "15px", fontWeight: "700", margin: "0 0 2px" }}>
                      {pkg.name}
                    </h3>
                    <div style={{ fontSize: "12px", color: "#6b7280" }}>
                      ${Number(pkg.packagePrice || 0).toFixed(2)} · {pkg.totalSessions} session
                      {pkg.totalSessions === 1 ? "" : "s"} · {subscribers.length} professional
                      {subscribers.length === 1 ? "" : "s"} subscribed
                    </div>
                  </div>

                  {sellability && !sellability.sellable ? (
                    <div
                      style={{
                        background: "#fef2f2",
                        border: "1px solid #fecaca",
                        borderRadius: "8px",
                        padding: "10px 12px",
                        marginBottom: "12px",
                      }}
                    >
                      <div
                        style={{
                          fontSize: "12px",
                          fontWeight: "700",
                          color: "#991b1b",
                          marginBottom: "4px",
                        }}
                      >
                        Not visible to customers
                      </div>
                      {(sellability.messages || []).map((message, index) => (
                        <div
                          key={index}
                          style={{ fontSize: "12px", color: "#7f1d1d", lineHeight: "1.45" }}
                        >
                          • {message}
                        </div>
                      ))}
                    </div>
                  ) : null}

                  {subscribers.length === 0 ? (
                    <p style={{ margin: 0, fontSize: "13px", color: "#6b7280" }}>
                      No professional has subscribed yet, so there is no one to set a rate for.
                    </p>
                  ) : (
                    <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
                      {subscribers.map((subscriber) => (
                        <div
                          key={subscriber.staffId}
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "8px",
                            fontSize: "13px",
                          }}
                        >
                          <span style={{ color: "#059669" }}>✓</span>
                          <span style={{ fontWeight: "600" }}>{subscriber.name}</span>
                          <span style={{ color: "#9ca3af", fontSize: "12px" }}>
                            — paid per completed session under their commission split
                          </span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </>
        )}

        {showAgreement ? (
          <PackageAgreementModal
            busy={busy}
            error={error}
            onAccept={acceptAgreement}
            onCancel={() => setShowAgreement(false)}
          />
        ) : null}
      </div>
    </SidebarLayout>
  );
};

export default PackageSettings;
